import {
  format,
  formatDistance,
  parseISO,
  isToday,
  isYesterday,
  differenceInMinutes,
} from 'date-fns';

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'MMM dd, yyyy');
}

export function formatTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'HH:mm a');
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'MMM dd, yyyy hh:mm a');
}

export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatDistance(dateObj, new Date(), { addSuffix: true });
}

export function getDayLabel(date: string | Date | null | undefined): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (isToday(dateObj)) return 'Today';
  if (isYesterday(dateObj)) return 'Yesterday';
  return format(dateObj, 'EEEE, MMM dd');
}

export function calculateDuration(
  startTime: string | Date | null | undefined,
  endTime: string | Date | null | undefined
): string {
  if (!startTime || !endTime) return '0h 0m';
  const start = typeof startTime === 'string' ? parseISO(startTime) : startTime;
  const end = typeof endTime === 'string' ? parseISO(endTime) : endTime;
  let minutes = differenceInMinutes(end, start);

  // Handle overnight shifts: if negative, add 24 hours (1440 minutes)
  if (minutes < 0) {
    minutes += 1440;
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

export function formatDuration(minutes: number): string {
  if (!minutes || minutes < 0) return '0h 0m';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

// Returns the effective end time for a shift, accounting for Saturday half-days
export function getEffectiveEndTime(shift: { endTime: string; isSaturdayHalfDay?: boolean; saturdayEndTime?: string }): string {
    if (!shift.endTime) return '';
    const isSaturday = new Date().getDay() === 6;
    if (isSaturday && shift.isSaturdayHalfDay && shift.saturdayEndTime) {
        return shift.saturdayEndTime;
    }
    return shift.endTime;
}

// Returns true when shift spans midnight (e.g. 17:00 – 02:00)
export function isOvernightShift(startTime: string, endTime: string): boolean {
    if (!startTime || !endTime) return false;
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    return eh * 60 + em < sh * 60 + sm;
}

// Returns how many minutes left until the shift starts
export function getMinutesUntilShiftStart(startTime: string, endTime?: string): number {
    if (!startTime) return 0;
    const now = new Date();
    const [sh, sm] = startTime.split(':').map(Number);
    const start = new Date();
    start.setHours(sh, sm, 0, 0);

    if (endTime && isOvernightShift(startTime, endTime)) {
        const [eh, em] = endTime.split(':').map(Number);
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        const endMinutes = eh * 60 + em;
        // If we are past midnight but before shift end time, the shift actually started yesterday
        if (nowMinutes < endMinutes) {
            start.setDate(start.getDate() - 1);
        }
    }

    // If the shift start is in the past, difference is negative.
    // If it's in the future, difference is positive.
    return differenceInMinutes(start, now);
}

// Returns how many minutes early the user is checking out (>0 means early)
export function getEarlyLeaveMinutes(startTime: string, endTime: string): number {
    if (!endTime) return 0;
    const [eh, em] = endTime.split(':').map(Number);
    const shiftEnd = new Date();
    shiftEnd.setHours(eh, em, 0, 0);
    // Overnight shift (e.g. 17:00–02:00): shift ends on the NEXT calendar day
    // when the current time is still in the evening portion (after shift start hour).
    if (isOvernightShift(startTime, endTime)) {
        const now = new Date();
        const [sh, sm] = startTime.split(':').map(Number);
        if (now.getHours() * 60 + now.getMinutes() >= sh * 60 + sm) {
            shiftEnd.setDate(shiftEnd.getDate() + 1);
        }
    }
    const diff = differenceInMinutes(shiftEnd, new Date());
    return diff > 0 ? diff : 0;
}
